import { css } from '@emotion/react';
import { semantic_colors, text_styles, colors, border_radius_styles_with_size } from '../../foundation';
import { RadioGroupVariant, AlignType, RadioVariantProps, RadioWidthType } from './RadioGroup';
import { RadioSize, RadioSizeUnion, DefaultRadioSizeUnion } from './types';
import { getInputHeightBySize } from '../input/styles';
import { buildCSSWithLength } from '../../styles';

const radio_box_disabled_style = {
  'border': `1px solid ${semantic_colors.border.disabled}`,
  'color': `${semantic_colors.content.disabled}`,
  'backgroundColor': `${semantic_colors.background.disabled}`,
  '+ label:not(.checked):not(:hover)': {
    borderLeft: `1px solid ${semantic_colors.border.disabled}`,
  },
  '&:hover': {
    backgroundColor: `${semantic_colors.background.disabled}`,
    border: `1px solid ${semantic_colors.border.disabled}`,
    color: `${semantic_colors.content.disabled}`,
  },
};

const radio_box_checked_style = {
  'border': `1px solid ${semantic_colors.accent.primary}`,
  'color': `${semantic_colors.accent.primary}`,
  '+ label': {
    borderLeft: `1px solid ${semantic_colors.accent.primary}`,
  },
};

export const getRadioGroupStyleByProps = ({
  variant,
  align,
  spacing,
  size,
}: {
  variant: RadioGroupVariant;
  align?: AlignType;
  spacing: Number;
  size?: RadioSizeUnion;
}) => {
  switch (variant) {
    case 'box':
      return css`
        display: flex;
      `;
    case 'default':
    default:
      return css`
        display: flex;
        flex-wrap: wrap;
        align-items: ${align === 'horizontal' ? 'center' : 'flex-start'};
        flex-direction: ${align === 'horizontal' ? 'row' : 'column'};
        row-gap: 16px;
        ${align === 'horizontal' ? `column-gap: ${spacing}px` : `row-gap: ${spacing}px`};
        ${align === 'horizontal' && buildCSSWithLength('min-height', getInputHeightBySize(size || RadioSize.medium))}
      `;
  }
};

export const default_radio_size_css: Record<DefaultRadioSizeUnion, Record<string, number | string>> = {
  small: {
    base: 14,
    fake: 8,
    spacing: 6,
    font: text_styles.Body_12_Regular,
    markMargin: '2px 0',
  },
  medium: {
    base: 18,
    fake: 10,
    spacing: 8,
    font: text_styles.Body_13_Regular,
    markMargin: '1px 0',
  },
  large: {
    base: 22,
    fake: 12,
    spacing: 10,
    font: text_styles.Body_14_Regular,
    markMargin: '0',
  },
};

export const getRadioWidthBySize = (size: RadioSizeUnion) => {
  const width: Record<RadioSizeUnion, number> = { xsmall: 56, small: 60, medium: 64, large: 70 };
  return width[size];
};

interface RadioStyleProps extends RadioVariantProps<RadioGroupVariant> {
  fill?: boolean;
  width?: RadioWidthType;
  disabled?: boolean;
  checked?: boolean;
  focused?: boolean;
}
export const getRadioStyleByProps = ({ variant, fill, width, size, disabled, checked, focused }: RadioStyleProps) => {
  const border_radius = border_radius_styles_with_size.small;
  const base_css = css`
    display: ${fill ? 'flex' : 'inline-flex'};
    ${width === 'fill' && 'flex-grow: 1'};
    align-items: center;
    cursor: ${disabled ? 'not-allowed' : 'pointer'};
    ${focused && `outline: 3px solid ${semantic_colors.tab_focus};`}
  `;
  switch (variant) {
    case 'box':
      const status_style = {
        ...(checked && radio_box_checked_style),
        ...(disabled && radio_box_disabled_style),
      };
      const boxSize = (size || RadioSize.medium) as RadioSizeUnion;
      return css`
        ${base_css};
        ${text_styles.Body_13_Regular};
        ${buildCSSWithLength('height', getInputHeightBySize(boxSize))}
        ${buildCSSWithLength('width', getRadioWidthBySize(boxSize))}

        display: inline-flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
        vertical-align: middle;
        color: ${semantic_colors.content.primary};
        border: 1px solid ${semantic_colors.border.secondary};
        background-color: ${semantic_colors.background.surface};
        ${focused && `z-index: 1;`}

        &:first-of-type {
          border-radius: ${border_radius}px 0 0 ${border_radius}px;
        }
        &:last-of-type {
          border-radius: 0 ${border_radius}px ${border_radius}px 0;
        }
        &:not(:first-of-type) {
          margin-left: -1px;
        }

        &:hover {
          border: 1px solid ${semantic_colors.accent.primary};
          color: ${semantic_colors.accent.primary};

          ${!disabled &&
          `+ label {
            border-left: 1px solid ${semantic_colors.accent.primary};
          }`}
        }

        ${status_style};
        ${checked &&
        disabled &&
        `
          background-color: ${colors.gray20};
          &:hover, &:active {
            background-color: ${colors.gray20};
          }
        `}
      `;
    case 'default':
    default:
      return css`
        &:hover:not(:has(label:hover, [data-role='radio-mark']:hover)) > [data-role='radio-mark']::before {
          border: solid 1px ${disabled ? semantic_colors.border.primary : semantic_colors.accent.primary};
        }

        &:hover > [data-role='radio-mark']:hover::before {
          border: solid 1px ${disabled ? semantic_colors.border.primary : semantic_colors.accent.primary};
        }

        ${base_css};
      `;
  }
};
